import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Check, Zap } from 'lucide-react';
import { toast } from 'sonner';

const Pricing = ({ user }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/payments/plans');
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    setProcessingPlan(planId);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/payments/create-checkout', {
        plan_id: planId,
        origin_url: originUrl
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ledger-bone flex items-center justify-center">
        <p className="font-mono text-sm text-gray-600">Loading plans...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ledger-bone relative noise-overlay">
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-black/10 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
            <h1 className="font-serif text-2xl font-bold tracking-tight text-ledger-green" data-testid="pricing-title">Choose Your Plan</h1>
            {user && (
              <span className="font-mono text-xs text-gray-600" data-testid="user-email">{user.email}</span>
            )}
          </div>
        </header>

        {/* Pricing Content */}
        <main className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-ledger-green mb-4">Simple, Transparent Pricing</h2>
            <p className="font-mono text-sm text-gray-600 mb-2">Choose the plan that works best for you</p>
            <p className="font-mono text-xs text-ledger-orange">After your free extraction, unlock unlimited conversions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.plan_id}
                className={`border-2 ${
                  plan.plan_id === 'annual' ? 'border-ledger-orange' : 'border-black/10'
                } bg-white p-8 relative`}
                data-testid={`plan-${plan.plan_id}`}
              >
                {plan.discount && (
                  <div className="absolute -top-3 right-8 bg-ledger-orange text-white px-4 py-1 font-mono text-xs uppercase tracking-widest">
                    {plan.discount}
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="font-serif text-2xl font-bold text-ledger-green mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-4xl font-bold text-ledger-green">${plan.amount.toFixed(2)}</span>
                    <span className="font-mono text-sm text-gray-600">/ {plan.interval}</span>
                  </div>
                  {plan.plan_id === 'annual' && (
                    <p className="font-mono text-xs text-gray-600 mt-1">Billed annually</p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-ledger-green flex-shrink-0 mt-0.5" />
                    <span className="font-mono text-sm text-gray-700">Unlimited statement uploads</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-ledger-green flex-shrink-0 mt-0.5" />
                    <span className="font-mono text-sm text-gray-700">AI-powered data extraction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-ledger-green flex-shrink-0 mt-0.5" />
                    <span className="font-mono text-sm text-gray-700">CSV export for all statements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-ledger-green flex-shrink-0 mt-0.5" />
                    <span className="font-mono text-sm text-gray-700">Secure cloud storage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-ledger-green flex-shrink-0 mt-0.5" />
                    <span className="font-mono text-sm text-gray-700">Priority support</span>
                  </li>
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.plan_id)}
                  disabled={processingPlan === plan.plan_id}
                  className={`w-full ${
                    plan.plan_id === 'annual' ? 'bg-ledger-orange' : 'bg-ledger-green'
                  } text-white rounded-none hover:opacity-90 uppercase tracking-widest text-xs py-4 px-8 font-mono disabled:opacity-50 flex items-center justify-center gap-2`}
                  data-testid={`select-${plan.plan_id}-btn`}
                >
                  {processingPlan === plan.plan_id ? (
                    'Processing...'
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Select {plan.name}
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* FAQ or Additional Info */}
          <div className="mt-16 border-2 border-black/10 bg-white p-8 max-w-2xl mx-auto">
            <h3 className="font-mono text-sm uppercase tracking-widest text-ledger-green mb-4">What's Included?</h3>
            <ul className="space-y-2 font-mono text-xs text-gray-600">
              <li>• Powered by Google Gemini 2.0 Flash AI</li>
              <li>• Supports PDF and image bank statements</li>
              <li>• Extract Date, Description, Amount, Balance</li>
              <li>• Download as clean CSV files</li>
              <li>• Cancel anytime, no questions asked</li>
            </ul>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Pricing;